import { type Edge, type Node, type NodeTypes, ReactFlow, ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';

interface GraphCanvasProps<TData extends Record<string, unknown>> {
  nodes: ReadonlyArray<Node<TData>>;
  edges: ReadonlyArray<Edge>;
  nodeTypes: NodeTypes;
}

/**
 * Controlled, read-only React Flow canvas. The graph is server-authored and driven entirely by the
 * `nodes`/`edges` props (positions come from the caller's layout), so there is no client editing OR
 * viewport interaction: node dragging, connecting, edge reconnection, selection, panning, and zoom
 * (scroll/pinch/double-click) are all disabled — the view is a fixed, fit-to-view snapshot. The caller
 * supplies `nodeTypes` and reads `nodes`/`edges` from a `GraphCanvasEngine` (via `createGraphCanvasContext`).
 *
 * Chrome-free: a plain light-gray background with no zoom/fit-view controls or readout — the graph is
 * fit to view on render. Wraps its own `ReactFlowProvider` so it is drop-in anywhere.
 */
export const GraphCanvas = <TData extends Record<string, unknown>>({
  nodes,
  edges,
  nodeTypes,
}: GraphCanvasProps<TData>): JSX.Element => (
  <div data-test-component="GraphCanvas" className="w-100 h-100 bg-light-gray">
    <ReactFlowProvider>
      <ReactFlow
        // React Flow's node/edge props are mutable-typed; it never mutates them, so widening the
        // store's ReadonlyArray snapshots here is safe.
        nodes={nodes as Array<Node<TData>>}
        edges={edges as Array<Edge>}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        edgesReconnectable={false}
        elementsSelectable={false}
        panOnDrag={false}
        panOnScroll={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        fitView
        proOptions={{ hideAttribution: true }}
      />
    </ReactFlowProvider>
  </div>
);
